import { useNavigate, useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { ParametricEditor } from '../components/ParametricEditor';
import { Message } from '@shared/types';
import { MessageItem, MeshUploadState } from '@/types/misc';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CurrentMessageContext } from '@/contexts/CurrentMessageContext';
import { SelectedItemsContext } from '@/contexts/SelectedItemsContext';
import { useConversation } from '@/services/conversationService';
import { BlobContext } from '@/contexts/BlobContext';
import { ColorContext } from '@/contexts/ColorContext';
import { useAutoThumbnailCapture } from '@/hooks/useAutoThumbnailCapture';

function AutoThumbnailCapture() {
  useAutoThumbnailCapture();
  return null;
}

export default function EditorView() {
  const { id: conversationId } = useParams();
  const [currentMessage, setCurrentMessage] = useState<Message | null>(null);
  const [images, setImages] = useState<MessageItem[]>([]);
  const [meshUpload, setMeshUpload] = useState<MeshUploadState | null>(null);
  const [blob, setBlob] = useState<Blob | null>(null);
  const [color, setColor] = useState<string>('#56B6C6');
  const navigate = useNavigate();
  const { conversation, isConversationLoading } = useConversation();
  const { t } = useTranslation('common');

  useEffect(() => {
    if (!conversationId) {
      navigate('/home');
    }
  }, [conversationId, navigate]);

  if (isConversationLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-synergy-bg-secondary-dark text-synergy-text-primary">
        <Loader2 className="h-8 w-8 animate-spin text-synergy-blue" />
      </div>
    );
  }

  if (!conversation.id) {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center bg-synergy-bg-secondary-dark text-synergy-text-primary">
        <span className="text-2xl font-medium">404</span>
        <span className="text-sm">{t('editor.notFound')}</span>
      </div>
    );
  }

  return (
    <CurrentMessageContext.Provider
      value={{
        currentMessage,
        setCurrentMessage,
      }}
    >
      <BlobContext.Provider value={{ blob, setBlob }}>
        <ColorContext.Provider value={{ color, setColor }}>
          <SelectedItemsContext.Provider
            value={{ images, setImages, meshUpload, setMeshUpload }}
          >
            <AutoThumbnailCapture />
            <ParametricEditor />
          </SelectedItemsContext.Provider>
        </ColorContext.Provider>
      </BlobContext.Provider>
    </CurrentMessageContext.Provider>
  );
}
